import { useIntl } from 'react-intl'

import ConfirmationDetails from './Common/ConfirmationDetails'
import Divider from './Common/Divider'
import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'
import LocationAndAddressWithMap from './Common/LocationAndAddressWithMap'
import StartAndEndDateTime from './Common/StartAndEndDateTime'
import SummaryAdditionalNotes from './Common/SummaryAdditionalNotes'
import SummaryDataItem from './Common/SummaryDataItem'
import DefaultSummaryModalFooter from './Common/SummaryModalFooter/DefaultSummaryModalFooter'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { TransportItem } from 'tripPlanner/types/tripItem'

interface Props {
  transportName: string
  item: TransportItem
}

function BaseTransportSummary(props: Props) {
  const { item, transportName } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()

  const headerTitle = intl.formatMessage(
    {
      id: '6iNnjOImxTIxYWNBHvod',
      defaultMessage: '{transportName} from {startPlace} to {endPlace}',
      description: 'Header title for transport trip item summary showing transport type and route',
    },
    {
      transportName,
      startPlace: item.startPlace.name,
      endPlace: item.endPlace.name,
    },
  )
  const departureLabel = intl.formatMessage({
    id: 'haiXg4VhpGwJNjZl1Chb',
    defaultMessage: 'Departure',
    description: 'Label for the departure date/time in transport trip item summary',
  })
  const arrivalLabel = intl.formatMessage({
    id: 'yxb_PQ-oi5d17B67Ak0q',
    defaultMessage: 'Arrival',
    description: 'Label for the arrival date/time in transport trip item summary',
  })
  const originLocationLabel = intl.formatMessage({
    id: 'wwru0hZYKboJqGws5u28',
    defaultMessage: 'Origin location',
    description: 'Label for the origin location section in transport trip item summary',
  })
  const destinationLocationLabel = intl.formatMessage({
    id: 'QobEAXcxc96nsXAMn2Ub',
    defaultMessage: 'Destination location',
    description: 'Label for the destination location section in transport trip item summary',
  })
  const seatLabel = intl.formatMessage({
    id: '976L1ErECwl_O8wwzo8G',
    defaultMessage: 'Seat',
    description: 'Label for the seat number in transport trip item summary',
  })
  const websiteLabel = intl.formatMessage({
    id: '1fOzX6yh-ofA9DU-g4bR',
    defaultMessage: 'Website',
    description: 'Label for the website link in transport trip item summary',
  })

  return (
    <>
      <ModalHeader
        title={headerTitle}
        subtitle={item.operatorName}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            <ItemTypeAndBookedLabel item={item} />
            <StartAndEndDateTime
              startTitle={departureLabel}
              endTitle={arrivalLabel}
              item={item}
            />
          </VerticalSpacer>
          <Divider />
          <LocationAndAddressWithMap
            item={item}
            place={item.startPlace}
            placeTitle={originLocationLabel}
          />
          <Divider />
          <LocationAndAddressWithMap
            item={item}
            place={item.endPlace}
            placeTitle={destinationLocationLabel}
          />
          <ConfirmationDetails
            isBooked={item.isBooked}
            confirmationCode={item.confirmationCode}
            price={item.price}
          />
          <Divider />
          <SummaryDataItem title={seatLabel} value={item.seat} />
          <Divider />
          <VerticalSpacer gap={20}>
            <SummaryDataItem title={websiteLabel} value={item.url} url={item.url} />
            <SummaryAdditionalNotes notes={item.notes} />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <DefaultSummaryModalFooter item={item} />
    </>
  )
}

export default BaseTransportSummary
